import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { parseContract } from '@/lib/api/parse';
import { createPresignedUploader, type UploadFn } from '@/lib/upload';

import {
  eligibleDevicesResponseSchema,
  presignedUploadSchema,
  toDeviceOption,
  toWarrantyClaimDetail,
  toWarrantyClaimSummary,
  warrantyClaimDetailWireSchema,
  warrantyClaimsResponseSchema,
  type CreateWarrantyClaimPayload,
  type DeviceOption,
  type WarrantyClaimDetail,
  type WarrantyClaimsPage,
} from './schemas';

/**
 * One page of Warranty claims — a Device's (113581 §A) or the whole account's (113581 §B).
 *
 * **One fetcher, two scopes.** Pass a `customerDeviceId` for that Device's claims, omit it for
 * every claim on the account: the two endpoints differ only in scope, never in shape, and
 * `meta.canRaiseClaim` reads per-Device or account-wide accordingly (ADR-0019) — the same field
 * answering the same question at whichever scope was asked. Account-wide it is what the routing
 * rule and the pinned CTA need: "is **some** Device eligible".
 *
 * **`getList`, not `get`** — `get` peels the `{ data }` envelope and would throw `meta` away with
 * it, and `meta` is what tells us whether another page exists. `fields/api.ts` does the same for
 * the same reason: `getList`'s static `PageMeta` is not this route's meta, so the raw value goes
 * straight into zod, which produces the real shape.
 *
 * No client-side sort. The server orders `requested_at DESC` (ADR-0019); sorting a single page
 * would be wrong the moment a second one exists.
 */
export async function fetchWarrantyClaims(
  cursor: string | null,
  limit: number,
  customerDeviceId?: string,
): Promise<WarrantyClaimsPage> {
  const path = customerDeviceId
    ? endpoints.warranty.deviceClaims(customerDeviceId)
    : endpoints.warranty.claims;
  const payload = parseContract(
    warrantyClaimsResponseSchema,
    // The server's whitelist rejects any other query key with a 400 — `page`/`pageSize` included.
    await apiClient.getList(path, { params: { limit, ...(cursor ? { cursor } : {}) } }),
    path,
  );

  return {
    hasEligibleDevices: payload.meta.canRaiseClaim,
    claims: payload.data.claims.map(toWarrantyClaimSummary),
    nextCursor: payload.meta.nextCursor,
  };
}

/** Raise one Warranty claim. The server answers `{ data: … }`; nothing here reads the body. */
export async function createWarrantyClaim(payload: CreateWarrantyClaimPayload): Promise<void> {
  await apiClient.post(endpoints.warranty.claims, payload);
}

/**
 * An `UploadFn` for a claim's evidence images: presign, then PUT the bytes straight to storage.
 *
 * `createPresignedUploader` already owns the PUT — including the off-origin bare-axios call, the
 * progress events and the abort signal — so this only supplies the presign half. `entityType` and
 * the `assetId` -> `imageAssetIds` handshake are the same across all three realms (grilling Q7).
 *
 * `customerDeviceId` is **optional** because the account-wide flow may attach images before a
 * Device has been chosen; the Device flow still sends it, so Q7's decision stands wherever it can.
 * Amendment flagged to BE as part of B5 (ADR-0019 § Presign).
 */
export function createWarrantyImageUploader(customerDeviceId?: string): UploadFn {
  return createPresignedUploader({
    getPresignedUrl: async (file) => {
      const path = endpoints.storage.presignedUrl;
      const presigned = parseContract(
        presignedUploadSchema,
        await apiClient.post(path, {
          fileName: file.name,
          fileType: file.type,
          entityType: 'warranty_claim',
          // Plain key, no conditional spread: axios `JSON.stringify`s a plain-object body, and
          // `JSON.stringify` omits a key whose value is `undefined`, so it never reaches the wire.
          customerDeviceId,
        }),
        path,
      );

      return {
        uploadUrl: presigned.uploadUrl,
        putHeaders: presigned.requiredHeaders ?? undefined,
        // `assetId` becomes `UploadedFile.id`, which is what the claim POSTs as `imageAssetIds`.
        file: {
          id: presigned.assetId,
          url: presigned.uploadUrl,
          name: file.name,
          size: file.size,
          contentType: file.type,
        },
      };
    },
  });
}

/**
 * The Devices a new claim may be raised against right now, as `<Select>` options.
 *
 * `get`, not `getList`: this route carries no `meta`, and `apiClient.get` peels the `{ data }`
 * envelope for us (ADR-0008). Nothing filters the result — the array **is** the server's verdict,
 * and a Device that cannot be claimed against is simply absent from it.
 */
export async function fetchEligibleDevices(): Promise<DeviceOption[]> {
  const path = endpoints.warranty.eligibleDevices;
  const devices = parseContract(eligibleDevicesResponseSchema, await apiClient.get(path), path);
  return devices.map(toDeviceOption);
}

/**
 * One Warranty claim in full, for the Ticket Preview.
 *
 * `get`, not `getList` — this is a single object and `get` peels the `{ data }` envelope
 * (ADR-0008), so the schema is the bare claim. The mirror of `fetchWarrantyClaims` above, which
 * needs `getList` precisely because it must keep `meta`.
 */
export async function fetchWarrantyClaim(claimId: string): Promise<WarrantyClaimDetail> {
  const path = endpoints.warranty.claim(claimId);
  return toWarrantyClaimDetail(
    parseContract(warrantyClaimDetailWireSchema, await apiClient.get(path), path),
  );
}
